import { createReducer, createActions } from "reduxsauce";
import Immutable from "seamless-immutable";

const normalizeAudienceBatchesList = (batches) => {
    if (Array.isArray(batches)) {
        return batches;
    }
    if (batches?.content && Array.isArray(batches.content)) {
        return batches.content;
    }
    return [];
};

const pruneApplyingBatchNos = (applyingBatchNos, audienceBatches) => {
    const settledBatchNos = new Set(
        normalizeAudienceBatchesList(audienceBatches)
            .filter((batch) => {
                const status = String(batch?.status ?? "").toUpperCase();
                return status === "COMPLETED" || status === "NOT_STARTED" || batch?.completedOn;
            })
            .map((batch) => String(batch.batchNo ?? ""))
    );

    return (applyingBatchNos || []).filter((batchNo) => !settledBatchNos.has(String(batchNo)));
};

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
    businessCustomerSubscriptionAmountsRequest: ["bcId"],
    businessCustomerAllRequest: ["businessId", "options"],
    exactFilteredBusinessCustomerRequest: ["businessId", "query", "options"],
    businessCustomerSearchRequest: ["businessId", "query"],
    businessCustomerSummarySearchRequest: ["businessId", "query"],
    businessCustomerSummaryRequest: ["businessId", 'options'],
    customerSummaryRequest: ["bcId"],
    businessCustomerUpdateRequest: ['businessCustomerId', 'businessCustomer'],
    businessCustomerSubscriptionsRequest: ["businessCustomerId"],
    businessCustomerRecordRequest: ["businessCustomerId"],
    businessCustomerSubscriptionsCountRequest: ["businessCustomerId"],
    businessCustomerUploadRequest: ["businessId", "audienceId", "file"],
    businessCustomerBulkPlanInviteRequest: ["businessId", "inviteData"],
    businessCustomerBulkInvitationRequest: ["targetListId"],
    businessCustomerBulkResendInvitationRequest: ["id"],
    businessCustomerImportRequest: ["businessId", "options"],
    businessCustomerImportCountRequest: ["businessId"],
    createAudienceRequest: ["audience"],
    audienceRequest: ["audienceId"],
    audienceBatchesRequest: ["audienceId"],
    audienceMembersRequest: ["audienceId", "options"],
    addAudienceMemberRequest: ["audienceId", "businessCustomerId"],
    removeAudienceMemberRequest: ["audienceId", "memberId"],
    audienceCandidateSegmentsRequest: ["audienceId"],
    audienceCandidateTagsRequest: ["audienceId"],
    saveAudienceCandidateMetadataRequest: ["audienceId", "metadata"],
    applyAudienceCandidatesRequest: ["audienceId"],
    downloadAudienceMembersCsvRequest: ["audienceId", "filename"],
    deleteAudienceCandidateSegmentRequest: ["audienceId", "candidateSegmentId"],
    deleteAudienceCandidateTagRequest: ["audienceId", "candidateTagId"],
    businessCustomerEntryRequest: ["importId",],
    businessCustomerApplyRequest: ["id", "batchNo"],
    businessCustomersBulkPauseRequest: ["membershipId", "ids"],
    businessCustomersBulkCancelRequest: ["membershipId", "ids"],
    businessCustomersSinglePauseRequest: ["businessCustomerId", "membershipId"],
    businessCustomersSingleCancelRequest: ["businessCustomerId", "membershipId"],
    businessCustomerPaymentsRequest: ["businessCustomerId", "size"],
    businessCustomerMissedPaymentsRequest: ["businessCustomerId", "size"],
    businessCustomerSubscriptionActivityRequest: ["businessCustomerId", "size"],
    customerReceivedPaymentRequest: ["businessCustomerId", "options"],
    customerMissedPaymentRequest: ["businessCustomerId"],
    customerPendingPaymentRequest: ["businessCustomerId"],
    customerReportRequest: ["businessId", "options"],
    customerChartsRequest: ["businessId", "duration", "options"],
    successfulPaymentsRequest: ["bcId", "options"],
    expiredPaymentsRequest: ["bcId", "options"],
    downloadCsvRequest: [],
    exportBusinessCustomersCsvRequest: ["businessId", "query", "filename"],
    businessCustomerStatementRequest: ["businessCustomerId", "startDate", "endDate", "options"],
    businessCustomerStatementCsvRequest: ["businessCustomerId", "startDate", "endDate"],
    businessCustomerStatementPdfRequest: ["businessCustomerId", "startDate", "endDate"],
    createSegmentRequest: ["segment"],
    updateSegmentRequest: ["segmentId", "segment"],
    fetchingSegmentRequest: ["businessId", "options"],
    segmentSearchRequest: ["businessId", "name"],
    fetchingSegmentCountRequest: ["businessId"],
    addingBulkListToSegmentRequest: ["id", "segmentId"],
    addingSingleSubToSegmentRequest: ["segmentId", "bcId"],
    addingBulkSubToSegmentRequest: ["segmentId", "bcIds"],
    removingSubToSegmentRequest: ["bcId"],
    removingBulkSubToSegmentRequest: ["bcIds"],
    tagGroupGraphedRequest: ["businessId"],
    createTagGroupRequest: ["tagGroup"],
    updateTagGroupRequest: ["groupId", "tagGroup"],
    tagGroupRequest: ["businessId", "options"],
    tagGroupSearchRequest: ["businessId", "name"],
    createTagRequest: ["tag"],
    tagsRequest: ["businessId"],
    updateTagRequest: ["tagId", "tag"],
    associateSubscriberToTagRequest: ["tagId", "bcId"],
    bulkAssociateSubscriberToTagRequest: ["tagId", "bcIds"],
    dissociateSubscriberFromTagRequest: ["tagId", "bcId"],
    bulkDissociateSubscribersFromTagRequest: ["tagId", "bcIds"],
    createBusinessCustomerRequest: ["businessId", "inviteData"],
    linkPlanWithBusinessCustomerRequest: ["planId", "bcId"],
    linkBusinessCustomerToPrimaryRequest: ["secondaryBusinessCustomerId", "primaryBusinessCustomerId", "reason"],
    unlinkBusinessCustomerFromPrimaryRequest: ["secondaryBusinessCustomerId"],
    customerEnrichmentsRequest: ["businessId", "options"],
    customerEnrichmentItemsRequest: ["businessId", "requestId", "options"],
    customerEnrichmentUploadRequest: ["businessId", "file"],

    businessCustomerSubscriptionAmountsSuccess: ["businessCustomerSubscriptionAmounts"],
    businessCustomerAllSuccess: ["businessCustomers"],
    exactFilteredBusinessCustomerSuccess: ["customers"],
    businessCustomerSummarySearchSuccess: ["businessCustomerSummarySearch"],
    businessCustomerSummarySuccess: ["businessCustomerSummary"],
    customerSummarySuccess: ["customerSummary"],
    businessCustomerSearchSuccess: ["customers"],
    businessCustomerUpdateSuccess: ['customer'],
    businessCustomerSubscriptionsSuccess: ["details"],
    businessCustomerRecordSuccess: ["customerRecord"],
    businessCustomerSubscriptionsCountSuccess: ["businessCustomerSubscriptionsCount"],
    businessCustomerUploadSuccess: ["customer"],
    businessCustomerBulkPlanInviteSuccess: ["details"],
    businessCustomerBulkInvitationSuccess: ["details"],
    businessCustomerBulkResendInvitationSuccess: ["details"],
    businessCustomerImportSuccess: ["importedCustomer"],
    businessCustomerImportCountSuccess: ["importedCustomerCount"],
    createAudienceSuccess: ["createdAudience"],
    audienceSuccess: ["audience"],
    audienceBatchesSuccess: ["audienceBatches"],
    audienceMembersSuccess: ["audienceMembers"],
    customerEnrichmentsSuccess: ["customerEnrichments"],
    customerEnrichmentItemsSuccess: ["customerEnrichmentItems"],
    customerEnrichmentUploadSuccess: ["enrichmentUpload"],
    addAudienceMemberSuccess: ["addedAudienceMember"],
    removeAudienceMemberSuccess: ["removedAudienceMember"],
    audienceCandidateSegmentsSuccess: ["audienceCandidateSegments"],
    audienceCandidateTagsSuccess: ["audienceCandidateTags"],
    saveAudienceCandidateMetadataSuccess: ["audienceCandidateMetadataSaved"],
    applyAudienceCandidatesSuccess: ["audienceCandidatesApplied"],
    downloadAudienceMembersCsvSuccess: ["csv"],
    deleteAudienceCandidateSegmentSuccess: ["deletedAudienceCandidateSegment"],
    deleteAudienceCandidateTagSuccess: ["deletedAudienceCandidateTag"],
    businessCustomersBulkPauseSuccess: ["pausedCustomers"],
    businessCustomersBulkCancelSuccess: ["cancelledCustomers"],
    businessCustomersSinglePauseSuccess: ["pausedCustomers"],
    businessCustomersSingleCancelSuccess: ["cancelledCustomers"],
    businessCustomerEntrySuccess: ["entry"],
    businessCustomerApplySuccess: ["appliedImport", "batchNo"],
    businessCustomerPaymentsSuccess: ["recentPayments"],
    businessCustomerMissedPaymentsSuccess: ["recentMissedPayments"],
    businessCustomerSubscriptionActivitySuccess: ["recentSubscriptionActivities"],
    customerReceivedPaymentSuccess: ["customerReceivedPayments"],
    customerMissedPaymentSuccess: ["customerMissedPayments"],
    customerPendingPaymentSuccess: ["customerPendingPayments"],
    customerReportSuccess: ["customerReports"],
    customerChartsSuccess: ["customerCharts"],
    successfulPaymentsSuccess: ["successfulPayments"],
    expiredPaymentsSuccess: ["expiredPayments"],
    downloadCsvSuccess: ["csv"],
    exportBusinessCustomersCsvSuccess: ["csv"],
    businessCustomerStatementSuccess: ["statement"],
    businessCustomerStatementCsvSuccess: ["csv"],
    businessCustomerStatementPdfSuccess: ["pdf"],
    bulkListToSegmentSuccess: ["data"],
    segmentCreateSuccess: ["data"],
    updateSegmentSuccess: ["data"],
    segmentSuccess: ["segment"],
    segmentSearchSuccess: ["segments"],
    segmentCountSuccess: ["count"],
    addSingleSubToSegmentSuccess: ["details"],
    addBulkSubToSegmentSuccess: ["details"],
    removeSubToSegmentSuccess: ["details"],
    removeBulkSubToSegmentSuccess: ["details"],
    tagGroupGraphedSuccess: ["tagGroupsGraphed"],
    createTagGroupSuccess: ["tagGroupContent"],
    updateTagGroupSuccess: ["tagGroup"],
    tagGroupSuccess: ["tagGroups"],
    tagGroupSearchSuccess: ["tagGroups"],
    createTagSuccess: ["tagContent"],
    updateTagSuccess: ["tag"],
    tagsSuccess: ["tags"],
    associateSubscriberToTagSuccess: ["associatedTag"],
    bulkAssociateSubscriberToTagSuccess: ["bulkAssociatedTag"],
    dissociateSubscriberFromTagSuccess: ["dissociatedTag"],
    bulkDissociateSubscribersFromTagSuccess: ["bulkDissociatedTag"],
    createBusinessCustomerSuccess: ["createdBusinessCustomer"],

    businessCustomerSubscriptionAmountsFailure: ["error"],
    businessCustomerAllFailure: ["error"],
    exactFilteredBusinessCustomersFailure: ["error"],
    businessCustomerSummarySearchFailure: ["error"],
    businessCustomerSummaryFailure: ["error"],
    customerSummaryFailure: ["error"],
    businessCustomerSearchFailure: ["error"],
    businessCustomerUpdateFailure: ['error'],
    businessCustomerSubscriptionsFailure: ["error"],
    businessCustomerRecordFailure: ["error"],
    businessCustomerSubscriptionsCountFailure: ["error"],
    businessCustomerUploadFailure: ["error"],
    businessCustomerBulkPlanInviteFailure: ["error"],
    businessCustomerBulkInvitationFailure: ["error"],
    businessCustomerBulkResendInvitationFailure: ["error"],
    businessCustomersBulkPauseFailure: ["error"],
    businessCustomersBulkCancelFailure: ["error"],
    businessCustomersSinglePauseFailure: ["error"],
    businessCustomersSingleCancelFailure: ["error"],
    businessCustomerImportFailure: ["error"],
    businessCustomerImportCountFailure: ["error"],
    createAudienceFailure: ["error"],
    audienceFailure: ["error"],
    audienceBatchesFailure: ["error"],
    audienceMembersFailure: ["error"],
    customerEnrichmentsFailure: ["error"],
    customerEnrichmentItemsFailure: ["error"],
    customerEnrichmentUploadFailure: ["error"],
    addAudienceMemberFailure: ["error"],
    removeAudienceMemberFailure: ["error"],
    audienceCandidateSegmentsFailure: ["error"],
    audienceCandidateTagsFailure: ["error"],
    saveAudienceCandidateMetadataFailure: ["error"],
    applyAudienceCandidatesFailure: ["error"],
    deleteAudienceCandidateSegmentFailure: ["error"],
    deleteAudienceCandidateTagFailure: ["error"],
    businessCustomerEntryFailure: ["error"],
    businessCustomerApplyFailure: ["error", "batchNo"],
    businessCustomerPaymentsFailure: ["error"],
    businessCustomerMissedPaymentsFailure: ["error"],
    businessCustomerSubscriptionActivityFailure: ["error"],
    customerReceivedPaymentFailure: ["error"],
    customerMissedPaymentFailure: ["error"],
    customerPendingPaymentFailure: ["error"],
    customerReportFailure: ["error"],
    customerChartsFailure: ["error"],
    successfulPaymentsFailure: ["error"],
    expiredPaymentsFailure: ["error"],
    downloadCsvFailure: ["error"],
    downloadAudienceMembersCsvFailure: ["error"],
    exportBusinessCustomersCsvFailure: ["error"],
    businessCustomerStatementFailure: ["error"],
    businessCustomerStatementCsvFailure: ["error"],
    businessCustomerStatementPdfFailure: ["error"],
    bulkListToSegmentFailure: ["error"],
    segmentCreateFailure: ["error"],
    updateSegmentFailure: ["error"],
    segmentFailure: ["error"],
    segmentSearchFailure: ["error"],
    segmentCountFailure: ["error"],
    addSingleSubToSegmentFailure: ["error"],
    addBulkSubToSegmentFailure: ["error"],
    removeSubToSegmentFailure: ["error"],
    removeBulkSubToSegmentFailure: ["error"],
    tagGroupGraphedFailure: ["error"],
    createTagGroupFailure: ["error"],
    tagGroupFailure: ["error"],
    tagGroupSearchFailure: ["error"],
    createTagFailure: ["error"],
    updateTagGroupFailure: ["error"],
    updateTagFailure: ["error"],
    tagsFailure: ["error"],
    associateSubscriberToTagFailure: ["error"],
    bulkAssociateSubscriberToTagFailure: ["error"],
    dissociateSubscriberFromTagFailure: ["error"],
    bulkDissociateSubscribersFromTagFailure: ["error"],
    linkPlanWithBusinessCustomerSuccess: ["result"],
    linkPlanWithBusinessCustomerFailure: ["error"],
    linkBusinessCustomerToPrimarySuccess: ["businessCustomer"],
    linkBusinessCustomerToPrimaryFailure: ["error"],
    unlinkBusinessCustomerFromPrimarySuccess: ["businessCustomer"],
    unlinkBusinessCustomerFromPrimaryFailure: ["error"],
    createBusinessCustomerFailure: ["error"]

});
export const BusinessCustomerTypes = Types;
export default Creators;


/* ------------- Initial State ------------- */

export const INITIAL_STATE = Immutable({
    fetchingAllBusinessCustomers: false,
    fetchingFilteredCustomers: null,
    fetchingExactFilteredBusinessCustomers: null,
    updating: false,
    fetchingbusinessCustomerSubscriptions: false,
    fetchingBusinessCustomerSummarySearch: false,
    fetchingBusinessCustomerSummary: false,
    fetchingCustomerSummary: false,
    fetchingBusinessCustomerRecord: false,
    pausingBulkBusinessCustomers: false,
    cancellingBulkBusinessCustomers: false,
    pausingBusinessCustomersSinglePause: false,
    cancellingSingleBusinessCustomer: false,
    fetchingCount: null,
    fetchingImportedCustomers: null,
    fetchingImportedCustomersCount: null,
    fetchingEntries: null,
    uploading: false,
    applying: false,
    applyingBatchNos: [],
    fetchingRecentPayments: false,
    fetchingRecentMissedPayments: false,
    fetchingRecentSubscriptionActivity: false,
    fetchingCustomerReceivedPayments: false,
    fetchingCustomerReceivedPayment: false,
    fetchingCustomerMissedPayments: false,
    fetchingCustomerPendingPayments: false,
    fetchingCustomerReports: false,
    fetchingCustomerCharts: false,
    fetchingSuccessfulPayments: false,
    fetchingExpiredPayments: false,
    fetchingBulkPlanInvite: false,
    fetchingBulkPlanInviteResend: false,
    fetchingCustomerListInvitation: false,
    fetchingBusinessCustomerSubscriptionAmounts: false,
    downloadingCsv: false,
    downloadingAudienceMembersCsv: false,
    exportingBusinessCustomersCsv: false,
    fetchingBusinessCustomerStatement: false,
    fetchingBusinessCustomerStatementCsv: false,
    fetchingBusinessCustomerStatementPdf: false,
    addingBulkListToSegment: false,
    creatingSegment: false,
    updatingSegment: false,
    fetchingSegment: false,
    searchingSegments: false,
    fetchingSegmentCount: false,
    addingSingleSubToSegment: false,
    addingBulkSubToSegment: false,
    removingSubToSegment: false,
    removingBulkSubToSegment: false,
    fetchingTagGroupsGraphed: false,
    creatingTagGroup: false,
    updatingTagGroup: false,
    fetchingTagGroups: false,
    searchingTagGroups: false,
    creatingTag: false,
    fetchingTags: false,
    associatingTag: false,
    bulkAssociatingTag: false,
    dissociatingTag: false,
    bulkDissociatingTag: false,
    updatingTag: false,
    linkingPlanWithBusinessCustomer: false,
    linkingBusinessCustomerToPrimary: false,
    unlinkingBusinessCustomerFromPrimary: false,
    creatingAudience: false,
    fetchingAudience: false,
    fetchingAudienceBatches: false,
    fetchingAudienceMembers: false,
    addingAudienceMember: false,
    removingAudienceMember: false,
    fetchingAudienceCandidateSegments: false,
    fetchingAudienceCandidateTags: false,
    savingAudienceCandidateMetadata: false,
    applyingAudienceCandidates: false,
    deletingAudienceCandidateSegment: false,
    deletingAudienceCandidateTag: false,
    fetchingCustomerEnrichments: false,
    fetchingCustomerEnrichmentItems: false,
    uploadingCustomerEnrichment: false,

    businessCustomers: null,
    businessCustomerSummarySearch: null,
    businessCustomerSummary: null,
    customerSummary: null,
    filteredCustomers: null,
    exactFilteredBusinessCustomers: null,
    businessCustomersBulkPause: null,
    businessCustomersBulkCancel: null,
    businessCustomersSinglePause: null,
    businessCustomersSingleCancel: null,
    updateSuccess: null,
    businessCustomerSubcriptions: null,
    businessCustomerRecord: null,
    count: null,
    uploadCustomer: null,
    importedCustomer: null,
    importedCustomerCount: null,
    createdAudience: null,
    audience: null,
    audienceBatches: null,
    audienceMembers: null,
    customerEnrichments: null,
    customerEnrichmentItems: null,
    enrichmentUpload: null,
    addedAudienceMember: null,
    removedAudienceMember: null,
    audienceCandidateSegments: null,
    audienceCandidateTags: null,
    audienceCandidateMetadataSaved: null,
    audienceCandidatesApplied: null,
    deletedAudienceCandidateSegment: null,
    deletedAudienceCandidateTag: null,
    entries: null,
    appliedImport: null,
    recentPayments: null,
    recentMissedPayments: null,
    recentSubscriptionActivity: null,
    customerReceivedPayments: null,
    customerReceivedPayment: null,
    customerMissedPayments: null,
    customerPendingPayments: null,
    customerReports: null,
    customerCharts: null,
    successfulPayments: null,
    expiredPayments: null,
    bulkPlanInvite: null,
    bulkPlanInviteResend: null,
    customerListSuccess: null,
    businessCustomerSubscriptionAmounts: null,
    bulkListToSegment: null,
    downloadedCsv: null,
    downloadedAudienceMembersCsv: null,
    exportedBusinessCustomersCsv: null,
    businessCustomerStatement: null,
    businessCustomerStatementCsv: null,
    businessCustomerStatementPdf: null,
    createdSegment: null,
    updatedSegment: null,
    segmentSuccess: null,
    segmentListTotalElements: null,
    segmentSearchResults: null,
    segmentCountSuccess: null,
    addSingleSubToSegmentSuccess: null,
    addBulkSubToSegmentSuccess: null,
    removeSubToSegmentSuccess: null,
    removeBulkSubToSegmentSuccess: null,
    tagGroupsGraphed: null,
    createdTagGroup: null,
    updatedTagGroup: {},
    tagGroups: null,
    tagGroupListTotalElements: null,
    tagGroupSearchResults: null,
    createdTag: null,
    tags: null,
    associatedTag: null,
    bulkAssociatedTag: null,
    dissociatedTag: null,
    bulkDissociatedTag: null,
    updatedTag: {},

    creatingBusinessCustomer: false,
    createdBusinessCustomer: null,
    createBusinessCustomerError: null,

    businessCustomersError: null,
    businessCustomerSummarySearchError: null,
    businessCustomerSummaryError: null,
    customerSummaryError: null,
    filteredCustomersError: null,
    exactFilteredBusinessCustomersError: null,
    updateError: null,
    businessCustomerSubscriptionsError: null,
    businessCustomerRecordError: null,
    countError: null,
    uploadError: null,
    importError: null,
    importCountError: null,
    createAudienceError: null,
    audienceError: null,
    audienceBatchesError: null,
    audienceMembersError: null,
    customerEnrichmentsError: null,
    customerEnrichmentItemsError: null,
    enrichmentUploadError: null,
    addAudienceMemberError: null,
    removeAudienceMemberError: null,
    audienceCandidateSegmentsError: null,
    audienceCandidateTagsError: null,
    saveAudienceCandidateMetadataError: null,
    applyAudienceCandidatesError: null,
    deleteAudienceCandidateSegmentError: null,
    deleteAudienceCandidateTagError: null,
    entryError: null,
    appliedImportError: null,
    recentSubscriptionActivityError: null,
    recentMissedPaymentsError: null,
    recentPaymentsError: null,
    bulkPlanInviteFailure: null,
    bulkPlanInviteResendFailure: null,
    customerListFailure: null,
    businessCustomersBulkPauseError: null,
    businessCustomersBulkCancelError: null,
    businessCustomersSinglePauseError: null,
    businessCustomersSingleCancelError: null,
    customerReceivedPaymentsError: null,
    customerReceivedPaymentError: null,
    customerMissedPaymentsError: null,
    customerPendingPaymentsError: null,
    customerReportsError: null,
    customerChartsError: null,
    successfulPaymentsError: null,
    expiredPaymentsError: null,
    businessCustomerSubscriptionAmountsError: null,
    downloadCsvError: null,
    downloadAudienceMembersCsvError: null,
    exportBusinessCustomersCsvError: null,
    businessCustomerStatementError: null,
    businessCustomerStatementCsvError: null,
    businessCustomerStatementPdfError: null,
    bulkListToSegmentError: null,
    createSegmentError: null,
    updateSegmentError: null,
    segmentError: null,
    segmentSearchError: null,
    segmentCountError: null,
    addSingleSubToSegmentError: null,
    addBulkSubToSegmentError: null,
    removeSubToSegmentError: null,
    removeBulkSubToSegmentError: null,
    tagGroupsGraphedError: null,
    createTagGroupError: null,
    updateTagGroupError: null,
    tagGroupsError: null,
    tagGroupSearchError: null,
    createTagError: null,
    tagsError: null,
    associatingTagError: null,
    bulkAssociatingTagError: null,
    dissociatingTagError: null,
    bulkDissociatingTagError: null,
    errorLinkPlanWithBusinessCustomer: null,
    errorLinkBusinessCustomerToPrimary: null,
    errorUnlinkBusinessCustomerFromPrimary: null,
    updateTagError: null
});

/* ------------- Reducers ------------- */

// request data from an api
export const allRequest = (state) => {
    return { ...state, fetchingAllBusinessCustomers: true, businessCustomers: null }
};

// request data from an api
export const customerSearchRequest = (state) => {
    return { ...state, fetchingFilteredCustomers: true, filteredCustomers: null }
}

//request data from an api
export const exactFilteredBusinessCustomerRequest = (state) => {
    return { ...state, fetchingExactFilteredBusinessCustomers: true, exactFilteredBusinessCustomers: null }
}

// update request 
export const updateRequest = (state) => {
    return { updating: true, updateSuccess: null }
};
// request data from an api 
export const businessCustomerSubscriptionsRequest = (state) => { return { ...state, fetchingbusinessCustomerSubscriptions: true, businessCustomerSubcriptions: null } }

// request data from an api 
export const businessCustomersBulkPauseRequest = (state) => { return { ...state, pausingBulkBusinessCustomers: true, businessCustomersBulkPause: null } }

// request data from an api 
export const businessCustomersBulkCancelRequest = (state) => { return { ...state, cancellingBulkBusinessCustomers: true, businessCustomersBulkCancel: null } }
// request data from an api 
export const businessCustomersSinglePauseRequest = (state) => { return { ...state, pausingBusinessCustomersSinglePause: true, businessCustomersSinglePause: null } }

// request data from an api 
export const businessCustomersSingleCancelRequest = (state) => { return { ...state, cancellingSingleBusinessCustomer: true, businessCustomersSingleCancel: null } }

// request data from an api 
export const businessCustomerRecordRequest = (state) => { return { ...state, fetchingBusinessCustomerRecord: true, businessCustomerRecord: null } }

// request data from an api 
export const businessCustomerSummarySearchRequest = (state) => { return { ...state, fetchingBusinessCustomerSummarySearch: true, businessCustomerSummarySearch: null, businessCustomerSummarySearchFailure: null } }

// request data from an api 
export const businessCustomerSummaryRequest = (state) => { return { ...state, fetchingBusinessCustomerSummary: true, businessCustomerSummary: null, businessCustomerSummaryFailure: null } }

// request data from an api 
export const customerSummaryRequest = (state) => { return { ...state, fetchingCustomerSummary: true, customerSummary: null, customerSummaryFailure: null } }


// request data from an api 
export const countRequest = (state) => { return { ...state, fetchingCount: true, count: null } }

export const uploadRequest = (state) => {
    return { ...state, uploading: true, uploadCustomer: null, uploadError: null }
}
export const downloadCsvRequest = (state) => {
    return { ...state, downloadingCsv: true, downloadedCsv: null, downloadCsvError: null }
}

export const exportBusinessCustomersCsvRequest = (state) => {
    return { ...state, exportingBusinessCustomersCsv: true, exportedBusinessCustomersCsv: null, exportBusinessCustomersCsvError: null }
}

export const businessCustomerStatementRequest = (state) => {
    return {
        ...state,
        fetchingBusinessCustomerStatement: true,
        businessCustomerStatement: null,
        businessCustomerStatementError: null,
    }
}

export const businessCustomerStatementCsvRequest = (state) => {
    return {
        ...state,
        fetchingBusinessCustomerStatementCsv: true,
        businessCustomerStatementCsv: null,
        businessCustomerStatementCsvError: null,
    }
}

export const businessCustomerStatementPdfRequest = (state) => {
    return {
        ...state,
        fetchingBusinessCustomerStatementPdf: true,
        businessCustomerStatementPdf: null,
        businessCustomerStatementPdfError: null,
    }
}

export const downloadAudienceMembersCsvRequest = (state) => {
    return {
        ...state,
        downloadingAudienceMembersCsv: true,
        downloadedAudienceMembersCsv: null,
        downloadAudienceMembersCsvError: null,
    }
}

export const createSegmentRequest = (state) => {
    return { ...state, creatingSegment: true, createdSegment: null, createSegmentError: null }
}

export const updateSegmentRequest = (state) => {
    return { ...state, updatingSegment: true, updatedSegment: null, updateSegmentError: null };
};

export const fetchingSegmentRequest = (state) => {
    return {
        ...state,
        fetchingSegment: true,
        segmentSuccess: null,
        segmentListTotalElements: null,
        segmentError: null,
    };
};

export const segmentSearchRequest = (state) => {
    return {
        ...state,
        searchingSegments: true,
        segmentSearchResults: null,
        segmentSearchError: null,
    };
};

export const fetchingSegmentCountRequest = (state) => {
    return { ...state, fetchingSegmentCount: true, segmentCountSuccess: null, segmentCountError: null }
}

export const addingSingleSubToSegmentRequest = (state) => {
    return { ...state, addingSingleSubToSegment: true, addSingleSubToSegmentSuccess: null, addSingleSubToSegmentError: null }
}

export const addingBulkSubToSegmentRequest = (state) => {
    return { ...state, addingBulkSubToSegment: true, addBulkSubToSegmentSuccess: null, addBulkSubToSegmentError: null }
}

export const removingSubToSegmentRequest = (state) => {
    return { ...state, removingSubToSegment: true, removeSubToSegmentSuccess: null, removeSubToSegmentError: null }
}
export const removingBulkSubToSegmentRequest = (state) => {
    return { ...state, removingBulkSubToSegment: true, removeBulkSubToSegmentSuccess: null, removeBulkSubToSegmentError: null }
}

export const businessCustomerBulkPlanInviteRequest = (state) => {
    return { ...state, fetchingBulkPlanInvite: true, bulkPlanInvite: null, bulkPlanInviteFailure: null }
}

export const businessCustomerBulkResendInvitationRequest = (state) => {
    return { ...state, fetchingBulkPlanInviteResend: true, bulkPlanInviteResend: null, bulkPlanInviteResendFailure: null }
}

export const businessCustomerBulkInvitationRequest = (state) => {
    return { ...state, fetchingCustomerListInvitation: true, customerListSuccess: null, customerListFailure: null }
}

export const importRequest = (state) => {
    return { ...state, fetchingImportedCustomers: true, importCustomer: null }
}
export const importCountRequest = (state) => {
    return { ...state, fetchingImportedCustomersCount: true, importedCustomerCount: null }
}

export const createAudienceRequest = (state) => {
    return { ...state, creatingAudience: true, createdAudience: null, createAudienceError: null }
}

export const audienceRequest = (state) => {
    return {
        ...state,
        fetchingAudience: true,
        audience: state.audience ?? null,
        audienceError: null,
        audienceBatches: null,
        audienceBatchesError: null,
        applying: false,
        applyingBatchNos: [],
    }
}

export const audienceBatchesRequest = (state) => {
    return { ...state, fetchingAudienceBatches: true, audienceBatches: state.audienceBatches ?? null, audienceBatchesError: null }
}

export const audienceMembersRequest = (state) => {
    return { ...state, fetchingAudienceMembers: true, audienceMembers: null, audienceMembersError: null }
}

export const customerEnrichmentsRequest = (state) => {
    return { ...state, fetchingCustomerEnrichments: true, customerEnrichmentsError: null }
}

export const customerEnrichmentItemsRequest = (state) => {
    return { ...state, fetchingCustomerEnrichmentItems: true, customerEnrichmentItems: null, customerEnrichmentItemsError: null }
}

export const customerEnrichmentUploadRequest = (state) => {
    return { ...state, uploadingCustomerEnrichment: true, enrichmentUpload: null, enrichmentUploadError: null }
}

export const addAudienceMemberRequest = (state) => {
    return { ...state, addingAudienceMember: true, addedAudienceMember: null, addAudienceMemberError: null }
}

export const removeAudienceMemberRequest = (state) => {
    return { ...state, removingAudienceMember: true, removedAudienceMember: null, removeAudienceMemberError: null }
}

export const audienceCandidateSegmentsRequest = (state) => {
    return { ...state, fetchingAudienceCandidateSegments: true, audienceCandidateSegments: null, audienceCandidateSegmentsError: null }
}

export const audienceCandidateTagsRequest = (state) => {
    return { ...state, fetchingAudienceCandidateTags: true, audienceCandidateTags: null, audienceCandidateTagsError: null }
}

export const saveAudienceCandidateMetadataRequest = (state) => {
    return { ...state, savingAudienceCandidateMetadata: true, audienceCandidateMetadataSaved: null, saveAudienceCandidateMetadataError: null }
}

export const applyAudienceCandidatesRequest = (state) => {
    return { ...state, applyingAudienceCandidates: true, audienceCandidatesApplied: null, applyAudienceCandidatesError: null }
}

export const deleteAudienceCandidateSegmentRequest = (state) => {
    return { ...state, deletingAudienceCandidateSegment: true, deletedAudienceCandidateSegment: null, deleteAudienceCandidateSegmentError: null }
}

export const deleteAudienceCandidateTagRequest = (state) => {
    return { ...state, deletingAudienceCandidateTag: true, deletedAudienceCandidateTag: null, deleteAudienceCandidateTagError: null }
}

export const entryRequest = (state) => {
    return { ...state, fetchingEntries: true, entries: state.entries ?? null }
}

export const applyRequest = (state, action) => {
    const { batchNo } = action;
    const applyingBatchNos = batchNo != null && batchNo !== ""
        ? [...(state.applyingBatchNos || []), String(batchNo)]
        : (state.applyingBatchNos || []);
    return { ...state, applying: true, applyingBatchNos, appliedImport: null, appliedImportError: null }
}

export const businessCustomerPaymentsRequest = (state) => {
    return { ...state, fetchingRecentPayments: true, recentPayments: null, recentPaymentsError: null }
}

export const businessCustomerMissedPaymentsRequest = (state) => {
    return { ...state, fetchingRecentMissedPayments: true, recentMissedPayments: null, recentMissedPaymentsError: null }
}

export const businessCustomerSubscriptionActivityRequest = (state) => {
    return { ...state, fetchingRecentSubscriptionActivity: true, recentSubscriptionActivity: null, recentSubscriptionActivityError: null }
}

export const customerReceivedPaymentRequest = (state) => {
    return { ...state, fetchingCustomerReceivedPayments: true, customerReceivedPayments: null, customerReceivedPaymentsError: null }
}

export const customerMissedPaymentRequest = (state) => {
    return { ...state, fetchingCustomerMissedPayments: true, customerMissedPayments: null, customerMissedPaymentsError: null }
}

export const customerPendingPaymentRequest = (state) => {
    return { ...state, fetchingCustomerPendingPayments: true, customerPendingPayments: null, customerPendingPaymentsError: null }
}

export const customerReportsRequest = (state) => {
    return { ...state, fetchingCustomerReports: true, customerReports: null, customerReportsError: null }
}
export const customerChartsRequest = (state) => {
    return { ...state, fetchingCustomerCharts: true, customerCharts: null, customerChartsError: null }
}

export const successfulPaymentsRequest = (state) => {
    return { ...state, fetchingSuccessfulPayments: true, successfulPayments: null, successfulPaymentsError: null }
}
export const expiredPaymentsRequest = (state) => {
    return { ...state, fetchingExpiredPayments: true, expiredPayments: null, expiredPaymentsError: null }
}
//request data from api
export const businessCustomerSubscriptionAmountsRequest = (state) => {
    return { ...state, fetchingBusinessCustomerSubscriptionAmounts: true, businessCustomerSubscriptionAmounts: null }
}

// tag group graphed request  from api
export const tagGroupGraphedRequest = (state) => {
    return { ...state, fetchingTagGroupsGraphed: true, tagGroupsGraphed: null }
};

// create tagGroup request 
export const createTagGroupRequest = (state) => {
    return { ...state, creatingTagGroup: true, createdTagGroup: null }
};

// update tagGroup request 
export const updateTagGroupRequest = (state) => {
    return { ...state, updatingTagGroup: true, updatedTagGroup: {} };
};

// tag group request  from api
export const tagGroupRequest = (state) => {
    return {
        ...state,
        fetchingTagGroups: true,
        tagGroups: null,
        tagGroupListTotalElements: null,
        tagGroupsError: null,
    };
};

export const tagGroupSearchRequest = (state) => {
    return {
        ...state,
        searchingTagGroups: true,
        tagGroupSearchResults: null,
        tagGroupSearchError: null,
    };
};

// create tag request 
export const createTagRequest = (state) => {
    return { ...state, creatingTag: true, createdTag: null }
};

// tag request  from api
export const tagsRequest = (state) => {
    return { ...state, fetchingTags: true, tags: null, tagsError: null }
};

// associate tag request 
export const associateSubscriberToTagRequest = (state) => {
    return { ...state, associatingTag: true, associatedTag: null }
};

// update tag request 
export const updateTagRequest = (state) => {
    return { ...state, updatingTag: true, updatedTag: {} };
};

// bulk associate tag request 
export const bulkAssociateSubscriberToTagRequest = (state) => {
    return { ...state, bulkAssociatingTag: true, bulkAssociatedTag: null }
};

export const addingBulkListToSegmentRequest = (state) => {
    return { ...state, addingBulkListToSegment: true, bulkListToSegment: null, bulkListToSegmentError: null }
};

// dissociate tag request 
export const dissociateSubscriberFromTagRequest = (state) => {
    return { ...state, dissociatingTag: true, dissociatedTag: null }
};

// bulk dissociate tag request 
export const bulkDissociateSubscribersFromTagRequest = (state) => {
    return { ...state, bulkDissociatingTag: true, bulkDissociatedTag: null }
};

// successful api lookup for single entity
export const allSuccess = (state, action) => {
    const { businessCustomers } = action;
    return { ...state, fetchingAllBusinessCustomers: false, businessCustomersError: null, businessCustomers }
};

// successful api lookup for exact filtered customers
export const exactFilteredBusinessCustomerSuccess = (state, action) => {
    const { customers } = action
    return {
        ...state,
        fetchingExactFilteredBusinessCustomers: false,
        exactFilteredBusinessCustomersError: null,
        exactFilteredBusinessCustomers: customers
    }
}
// succesful api lookup for filtered customers
export const customerSearchSuccess = (state, action) => {
    const { customers } = action
    return {
        ...state,
        fetchingFilteredCustomers: false,
        filteredCustomersError: null,
        filteredCustomers: customers
    }
}

// successful update
export const updateSuccess = (state, action) => {
    const { customer } = action;
    return { ...state, updating: false, updateError: null, updateSuccess: customer }
};
// successful api lookup for business customer subscriptions 
export const businessCustomerSubcriptionsSuccess = (state, action) => {
    const { details } = action
    return {
        ...state,
        fetchingbusinessCustomerSubscriptions: false,
        businessCustomerSubcriptions: details,
        businessCustomerSubscriptionsError: null
    }
}

// successful api lookup for business customer record
export const businessCustomerRecordSuccess = (state, action) => {
    const { customerRecord } = action
    return {
        ...state,
        fetchingBusinessCustomerRecord: false,
        businessCustomerRecord: customerRecord,
        businessCustomerRecordError: null
    }
}

// successful api lookup for business customers subscriptions count 
export const countSuccess = (state, action) => {
    const { businessCustomerSubscriptionsCount } = action
    return {
        ...state,
        fetchingCount: false,
        count: businessCustomerSubscriptionsCount,
        countError: null
    }
}

export const businessCustomersBulkPauseSuccess = (state, action) => {
    const { pausedCustomers } = action
    return {
        ...state, pausingBulkBusinessCustomers: false, businessCustomersBulkPause: pausedCustomers, businessCustomersBulkPauseError: null
    }
}
export const businessCustomersBulkCancelSuccess = (state, action) => {
    const { cancelledCustomers } = action
    return {
        ...state, cancellingBulkBusinessCustomers: false, businessCustomersBulkCancelError: null, businessCustomersBulkCancel: cancelledCustomers
    }
}
export const businessCustomersSinglePauseSuccess = (state, action) => {
    const { pausedCustomers } = action
    return {
        ...state, pausingBusinessCustomersSinglePause: false, businessCustomersSinglePause: pausedCustomers, businessCustomersSinglePauseError: null,
    }
}
export const businessCustomersSingleCancelSuccess = (state, action) => {
    const { cancelledCustomers } = action
    return {
        ...state, cancellingSingleBusinessCustomer: false, businessCustomersSingleCancelError: null, businessCustomersSingleCancel: cancelledCustomers
    }
}

export const uploadSuccess = (state, action) => {
    const { customer } = action;
    return { ...state, uploading: false, uploadCustomer: customer }
}

export const importSuccess = (state, action) => {
    const { importedCustomer } = action;
    return { ...state, fetchingImportedCustomers: false, importedCustomer: importedCustomer }
}
export const downloadCsvSuccess = (state, action) => {
    const { csv } = action;
    return { ...state, downloadingCsv: false, downloadedCsv: csv, downloadCsvError: null }
}

export const exportBusinessCustomersCsvSuccess = (state, action) => {
    const { csv } = action;
    return { ...state, exportingBusinessCustomersCsv: false, exportedBusinessCustomersCsv: csv, exportBusinessCustomersCsvError: null }
}

export const businessCustomerStatementSuccess = (state, action) => {
    const { statement } = action;
    return {
        ...state,
        fetchingBusinessCustomerStatement: false,
        businessCustomerStatement: statement,
        businessCustomerStatementError: null,
    }
}

export const businessCustomerStatementCsvSuccess = (state, action) => {
    const { csv } = action;
    return {
        ...state,
        fetchingBusinessCustomerStatementCsv: false,
        businessCustomerStatementCsv: csv,
        businessCustomerStatementCsvError: null,
    }
}

export const businessCustomerStatementPdfSuccess = (state, action) => {
    const { pdf } = action;
    return {
        ...state,
        fetchingBusinessCustomerStatementPdf: false,
        businessCustomerStatementPdf: pdf,
        businessCustomerStatementPdfError: null,
    }
}

export const downloadAudienceMembersCsvSuccess = (state, action) => {
    const { csv } = action;
    return {
        ...state,
        downloadingAudienceMembersCsv: false,
        downloadedAudienceMembersCsv: csv,
        downloadAudienceMembersCsvError: null,
    }
}
export const businessCustomerBulkPlanInviteSuccess = (state, action) => {
    const { details } = action;
    return { ...state, fetchingBulkPlanInvite: false, bulkPlanInvite: details, bulkPlanInviteFailure: null }

}

export const businessCustomerBulkResendInvitationSuccess = (state, action) => {
    const { details } = action;
    return { ...state, fetchingBulkPlanInviteResend: false, bulkPlanInviteResend: details, bulkPlanInviteResendFailure: null }
}

export const businessCustomerBulkInvitationSuccess = (state, action) => {
    const { details } = action;
    return { ...state, fetchingCustomerListInvitation: false, customerListSuccess: details, customerListFailure: null }

}

export const importCountSuccess = (state, action) => {
    const { importedCustomerCount } = action;
    return { ...state, fetchingImportedCustomersCount: false, importedCustomerCount: importedCustomerCount }
}

export const createAudienceSuccess = (state, action) => {
    const { createdAudience } = action;
    return { ...state, creatingAudience: false, createdAudience, createAudienceError: null }
}

export const audienceSuccess = (state, action) => {
    const { audience } = action;
    return { ...state, fetchingAudience: false, audience, audienceError: null }
}

export const audienceBatchesSuccess = (state, action) => {
    const { audienceBatches } = action;
    const applyingBatchNos = pruneApplyingBatchNos(state.applyingBatchNos, audienceBatches);
    return {
        ...state,
        fetchingAudienceBatches: false,
        audienceBatches,
        audienceBatchesError: null,
        applying: applyingBatchNos.length > 0,
        applyingBatchNos,
    }
}

export const audienceMembersSuccess = (state, action) => {
    const { audienceMembers } = action;
    return { ...state, fetchingAudienceMembers: false, audienceMembers, audienceMembersError: null }
}

export const customerEnrichmentsSuccess = (state, action) => {
    const { customerEnrichments } = action;
    return { ...state, fetchingCustomerEnrichments: false, customerEnrichments, customerEnrichmentsError: null }
}

export const customerEnrichmentItemsSuccess = (state, action) => {
    const { customerEnrichmentItems } = action;
    return { ...state, fetchingCustomerEnrichmentItems: false, customerEnrichmentItems, customerEnrichmentItemsError: null }
}

export const customerEnrichmentUploadSuccess = (state, action) => {
    const { enrichmentUpload } = action;
    return { ...state, uploadingCustomerEnrichment: false, enrichmentUpload, enrichmentUploadError: null }
}

export const addAudienceMemberSuccess = (state, action) => {
    const { addedAudienceMember } = action;
    return { ...state, addingAudienceMember: false, addedAudienceMember, addAudienceMemberError: null }
}

export const removeAudienceMemberSuccess = (state, action) => {
    const { removedAudienceMember } = action;
    return { ...state, removingAudienceMember: false, removedAudienceMember, removeAudienceMemberError: null }
}

export const audienceCandidateSegmentsSuccess = (state, action) => {
    const { audienceCandidateSegments } = action;
    return { ...state, fetchingAudienceCandidateSegments: false, audienceCandidateSegments, audienceCandidateSegmentsError: null }
}

export const audienceCandidateTagsSuccess = (state, action) => {
    const { audienceCandidateTags } = action;
    return { ...state, fetchingAudienceCandidateTags: false, audienceCandidateTags, audienceCandidateTagsError: null }
}

export const saveAudienceCandidateMetadataSuccess = (state, action) => {
    const { audienceCandidateMetadataSaved } = action;
    return { ...state, savingAudienceCandidateMetadata: false, audienceCandidateMetadataSaved, saveAudienceCandidateMetadataError: null }
}

export const applyAudienceCandidatesSuccess = (state, action) => {
    const { audienceCandidatesApplied } = action;
    return { ...state, applyingAudienceCandidates: false, audienceCandidatesApplied, applyAudienceCandidatesError: null }
}

export const deleteAudienceCandidateSegmentSuccess = (state, action) => {
    const { deletedAudienceCandidateSegment } = action;
    return { ...state, deletingAudienceCandidateSegment: false, deletedAudienceCandidateSegment, deleteAudienceCandidateSegmentError: null }
}

export const deleteAudienceCandidateTagSuccess = (state, action) => {
    const { deletedAudienceCandidateTag } = action;
    return { ...state, deletingAudienceCandidateTag: false, deletedAudienceCandidateTag, deleteAudienceCandidateTagError: null }
}

export const entrySuccess = (state, action) => {
    const { entry } = action;
    return { ...state, fetchingEntries: false, entries: entry }
}

export const applySuccess = (state, action) => {
    const { appliedImport, batchNo } = action;
    const applyingBatchNos = batchNo != null && batchNo !== ""
        ? (state.applyingBatchNos || []).filter((item) => String(item) !== String(batchNo))
        : (state.applyingBatchNos || []);
    return {
        ...state,
        applying: applyingBatchNos.length > 0,
        applyingBatchNos,
        appliedImport,
        appliedImportError: null,
    }
}


export const businessCustomerSummarySearchSuccess = (state, action) => {
    const { businessCustomerSummarySearch } = action
    return { ...state, fetchingBusinessCustomerSummarySearch: false, businessCustomerSummarySearch, businessCustomerSummarySearchFailure: null }
}

export const businessCustomerSummarySuccess = (state, action) => {
    const { businessCustomerSummary } = action
    return { ...state, fetchingBusinessCustomerSummary: false, businessCustomerSummary, businessCustomerSummaryFailure: null }
}
export const customerSummarySuccess = (state, action) => {
    const { customerSummary } = action
    return { ...state, fetchingCustomerSummary: false, customerSummary, customerSummaryFailure: null }
}

export const businessCustomerPaymentsSuccess = (state, action) => {
    const { recentPayments } = action
    return { ...state, fetchingRecentPayments: false, recentPayments: recentPayments, recentPaymentsError: null }
}
export const businessCustomerMissedPaymentsSuccess = (state, action) => {
    const { recentMissedPayments } = action
    return { ...state, fetchingRecentMissedPayments: false, recentMissedPayments, recentMissedPaymentsError: null }
}
export const businessCustomerSubscriptionActivitySuccess = (state, action) => {
    const { recentSubscriptionActivities } = action
    return { ...state, fetchingRecentSubscriptionActivity: false, recentSubscriptionActivity: recentSubscriptionActivities, recentSubscriptionActivityError: null }
}

export const customerReceivedPaymentSuccess = (state, action) => {
    const { customerReceivedPayments } = action
    return { ...state, fetchingCustomerReceivedPayments: false, customerReceivedPayments: customerReceivedPayments, customerReceivedPaymentsError: null }
}

export const segmentCreateSuccess = (state, action) => {
    const { data } = action
    return { ...state, creatingSegment: false, createdSegment: data, createSegmentError: null }
}

export const updateSegmentSuccess = (state, action) => {
    const { data } = action;
    return { ...state, updatingSegment: false, updatedSegment: data, updateSegmentError: null };
};

export const segmentSuccess = (state, action) => {
    const { segment } = action;
    const segmentListTotalElements =
        typeof segment?.totalElements === "number" ? segment.totalElements : null;
    return {
        ...state,
        fetchingSegment: false,
        segmentSuccess: segment,
        segmentListTotalElements,
        segmentError: null,
    };
};

export const segmentSearchSuccess = (state, action) => {
    const { segments } = action;
    return {
        ...state,
        searchingSegments: false,
        segmentSearchResults: segments,
        segmentSearchError: null,
    };
};

export const segmentCountSuccess = (state, action) => {
    const { count } = action
    return { ...state, fetchingSegmentCount: false, segmentCountSuccess: count, segmentCountError: null }
}

export const addSingleSubToSegmentSuccess = (state, action) => {
    const { details } = action
    return { ...state, addingSingleSubToSegment: false, addSingleSubToSegmentSuccess: details, addSingleSubToSegmentError: null }
}

export const bulkListToSegmentSuccess = (state, action) => {
    const { data } = action
    return { ...state, addingBulkListToSegment: false, bulkListToSegment: data, bulkListToSegmentError: null }
};

export const addBulkSubToSegmentSuccess = (state, action) => {
    const { details } = action
    return { ...state, addingBulkSubToSegment: false, addBulkSubToSegmentSuccess: details, addBulkSubToSegmentError: null }
}

export const removeSubToSegmentSuccess = (state, action) => {
    const { details } = action
    return { ...state, removingSubToSegment: false, removeSubToSegmentSuccess: details, removeSubToSegmentError: null }

}

export const removeBulkSubToSegmentSuccess = (state, action) => {
    const { details } = action
    return { ...state, removingBulkSubToSegment: false, removeBulkSubToSegmentSuccess: details, removeBulkSubToSegmentError: null }
}

export const customerMissedPaymentSuccess = (state, action) => {
    const { customerMissedPayments } = action
    return { ...state, fetchingCustomerMissedPayments: false, customerMissedPayments: customerMissedPayments, customerMissedPaymentsError: null }
}
export const customerPendingPaymentSuccess = (state, action) => {
    const { customerPendingPayments } = action
    return { ...state, fetchingCustomerPendingPayments: false, customerPendingPayments: customerPendingPayments, customerPendingPaymentsError: null }
}

export const successfulPaymentsSuccess = (state, action) => {
    const { successfulPayments } = action
    return { ...state, fetchingSuccessfulPayments: false, successfulPayments, successfulPaymentsError: null }
}
export const expiredPaymentsSuccess = (state, action) => {
    const { expiredPayments } = action
    return { ...state, fetchingExpiredPayments: false, expiredPayments: expiredPayments, expiredPaymentsError: null }
}
export const customerReportsSuccess = (state, action) => {
    const { customerReports } = action
    return { ...state, fetchingCustomerReports: false, customerReports: customerReports, customerReportsError: null }
}
export const customerChartsSuccess = (state, action) => {
    const { customerCharts } = action
    return { ...state, fetchingCustomerCharts: false, customerCharts: customerCharts, customerChartsError: null }
}

//successful api lookup for single entity
export const businessCustomerSubscriptionAmountsSuccess = (state, action) => {
    const { businessCustomerSubscriptionAmounts } = action;
    return {
        ...state, fetchingBusinessCustomerSubscriptionAmounts: false, businessCustomerSubscriptionAmountsError: null,
        businessCustomerSubscriptionAmounts
    }
}

// successful tag group graphed request from api
export const tagGroupGraphedSuccess = (state, action) => {
    const { tagGroupsGraphed } = action;
    return { ...state, fetchingTagGroupsGraphed: false, tagGroupsGraphedError: null, tagGroupsGraphed };
};

// successful tag group create
export const createTagGroupSuccess = (state, action) => {
    const { tagGroupContent } = action;
    return { ...state, creatingTagGroup: false, createTagGroupError: null, createdTagGroup: tagGroupContent };
};

// successful api update
export const updateTagGroupSuccess = (state, action) => {
    const { tagGroup } = action;
    return { ...state, updatingTagGroup: false, updateTagGroupError: null, updatedTagGroup: tagGroup };
};

// successful api update
export const updateTagSuccess = (state, action) => {
    const { tag } = action;
    return { ...state, updatingTag: false, updateTagError: null, updatedTag: tag };
};

// successful tag group request from api
export const tagGroupSuccess = (state, action) => {
    const { tagGroups } = action;
    const tagGroupListTotalElements =
        typeof tagGroups?.totalElements === "number" ? tagGroups.totalElements : null;
    return {
        ...state,
        fetchingTagGroups: false,
        tagGroupsError: null,
        tagGroups,
        tagGroupListTotalElements,
    };
};

export const tagGroupSearchSuccess = (state, action) => {
    const { tagGroups } = action;
    return {
        ...state,
        searchingTagGroups: false,
        tagGroupSearchResults: tagGroups,
        tagGroupSearchError: null,
    };
};

// successful tag create
export const createTagSuccess = (state, action) => {
    const { tagContent } = action;
    return { ...state, creatingTag: false, createTagError: null, createdTag: tagContent };
};

// successful tag request from api
export const tagsSuccess = (state, action) => {
    const { tags } = action;
    return { ...state, fetchingTags: false, tagsError: null, tags };
};

// successfully associated tag
export const associateSubscriberToTagSuccess = (state, action) => {
    const { associatedTag } = action;
    return { ...state, associatingTag: false, associatingTagError: null, associatedTag };
};

// successfully bulk associated tag
export const bulkAssociateSubscriberToTagSuccess = (state, action) => {
    const { bulkAssociatedTag } = action;
    return { ...state, bulkAssociatingTag: false, bulkAssociatingTagError: null, bulkAssociatedTag };
};

// successfully dissociated tag
export const dissociateSubscriberFromTagSuccess = (state, action) => {
    const { dissociatedTag } = action;
    return { ...state, dissociatingTag: false, dissociatingTagError: null, dissociatedTag };
};

// successfully bulk dissociated tag
export const bulkDissociateSubscribersFromTagSuccess = (state, action) => {
    const { bulkDissociatedTag } = action;
    return { ...state, bulkDissociatingTag: false, bulkDissociatingTagError: null, bulkDissociatedTag };
};

export const createBusinessCustomerRequest = (state) => {
    return { ...state, creatingBusinessCustomer: true, createdBusinessCustomer: null, createBusinessCustomerError: null }
};

export const createBusinessCustomerSuccess = (state, action) => {
    const { createdBusinessCustomer } = action
    return {
        ...state,
        creatingBusinessCustomer: false,
        createdBusinessCustomer,
        createBusinessCustomerError: null
    }
};

export const createBusinessCustomerFailure = (state, action) => {
    const { error } = action
    return {
        ...state,
        creatingBusinessCustomer: false,
        createdBusinessCustomer: null,
        createBusinessCustomerError: error
    }
};

export const linkPlanWithBusinessCustomerRequest = (state) => ({
    ...state,
    linkingPlanWithBusinessCustomer: true,
    errorLinkPlanWithBusinessCustomer: null,
});

export const linkPlanWithBusinessCustomerSuccess = (state) => ({
    ...state,
    linkingPlanWithBusinessCustomer: false,
    errorLinkPlanWithBusinessCustomer: null,
});

export const linkPlanWithBusinessCustomerFailure = (state, action) => {
    const { error } = action;
    return {
        ...state,
        linkingPlanWithBusinessCustomer: false,
        errorLinkPlanWithBusinessCustomer: error,
    };
};

export const linkBusinessCustomerToPrimaryRequest = (state) => ({
    ...state,
    linkingBusinessCustomerToPrimary: true,
    errorLinkBusinessCustomerToPrimary: null,
});

export const linkBusinessCustomerToPrimarySuccess = (state, action) => {
    const { businessCustomer } = action;
    return {
        ...state,
        linkingBusinessCustomerToPrimary: false,
        errorLinkBusinessCustomerToPrimary: null,
        businessCustomerRecord: businessCustomer || state.businessCustomerRecord,
    };
};

export const linkBusinessCustomerToPrimaryFailure = (state, action) => {
    const { error } = action;
    return {
        ...state,
        linkingBusinessCustomerToPrimary: false,
        errorLinkBusinessCustomerToPrimary: error,
    };
};

export const unlinkBusinessCustomerFromPrimaryRequest = (state) => ({
    ...state,
    unlinkingBusinessCustomerFromPrimary: true,
    errorUnlinkBusinessCustomerFromPrimary: null,
});

export const unlinkBusinessCustomerFromPrimarySuccess = (state, action) => {
    const { businessCustomer } = action;
    return {
        ...state,
        unlinkingBusinessCustomerFromPrimary: false,
        errorUnlinkBusinessCustomerFromPrimary: null,
        businessCustomerRecord: businessCustomer || state.businessCustomerRecord,
    };
};

export const unlinkBusinessCustomerFromPrimaryFailure = (state, action) => {
    const { error } = action;
    return {
        ...state,
        unlinkingBusinessCustomerFromPrimary: false,
        errorUnlinkBusinessCustomerFromPrimary: error,
    };
};


// Something went wrong fetching all count.
export const allFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingAllBusinessCustomers: false, businessCustomersError: error, businessCustomers: null }
};

// Something went wrong fetching filtered customers
export const customerSearchFailure = (state, action) => {
    const { error } = action
    return {
        ...state,
        fetchingFilteredCustomers: false,
        filteredCustomersError: error,
        filteredCustomers: null
    }
}
// Something went wrong fetching filtered customers
export const exactFilteredBusinessCustomersFailure = (state, action) => {
    const { error } = action
    return {
        ...state,
        fetchingExactFilteredBusinessCustomers: false,
        exactFilteredBusinessCustomersError: error,
        exactFilteredBusinessCustomers: null
    }
}

// Something went wrong updating.
export const updateFailure = (state, action) => {
    const { error } = action;
    return { ...state, updating: false, updateError: error, updateSuccess: null }
};

// Something went wrong fetching business customer subscriptions 
export const businessCustomerSubscriptionsFailure = (state, action) => {
    const { error } = action
    return {
        ...state,
        fetchingbusinessCustomerSubscriptions: false,
        businessCustomerSubcriptions: null,
        businessCustomerSubscriptionsError: error
    }
}

// Something went wrong fetching business customer record 
export const businessCustomerRecordFailure = (state, action) => {
    const { error } = action
    return {
        ...state,
        fetchingBusinessCustomerRecord: false,
        businessCustomerRecord: null,
        businessCustomerRecordError: error
    }
}

// Something went wrong fetching business customer subscriptions count
export const countFailure = (state, action) => {
    const { error } = action
    return {
        ...state,
        fetchingCount: false,
        count: null,
        countError: error
    }
}

export const uploadFailure = (state, action) => {
    const { error } = action;
    return { ...state, uploading: false, uploadError: error }
}

export const importFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingImportedCustomers: false, importError: error, importedCustomer: null }
}

export const downloadCsvFailure = (state, action) => {
    const { error } = action;
    return { ...state, downloadingCsv: false, downloadCsvError: error, downloadedCsv: null }
}

export const exportBusinessCustomersCsvFailure = (state, action) => {
    const { error } = action;
    return { ...state, exportingBusinessCustomersCsv: false, exportBusinessCustomersCsvError: error, exportedBusinessCustomersCsv: null }
}

export const businessCustomerStatementFailure = (state, action) => {
    const { error } = action;
    return {
        ...state,
        fetchingBusinessCustomerStatement: false,
        businessCustomerStatementError: error,
        businessCustomerStatement: null,
    }
}

export const businessCustomerStatementCsvFailure = (state, action) => {
    const { error } = action;
    return {
        ...state,
        fetchingBusinessCustomerStatementCsv: false,
        businessCustomerStatementCsvError: error,
        businessCustomerStatementCsv: null,
    }
}

export const businessCustomerStatementPdfFailure = (state, action) => {
    const { error } = action;
    return {
        ...state,
        fetchingBusinessCustomerStatementPdf: false,
        businessCustomerStatementPdfError: error,
        businessCustomerStatementPdf: null,
    }
}

export const downloadAudienceMembersCsvFailure = (state, action) => {
    const { error } = action;
    return {
        ...state,
        downloadingAudienceMembersCsv: false,
        downloadAudienceMembersCsvError: error,
        downloadedAudienceMembersCsv: null,
    }
}

export const segmentCreateFailure = (state, action) => {
    const { error } = action;
    return { ...state, creatingSegment: false, createdSegment: null, createSegmentError: error }
}

export const updateSegmentFailure = (state, action) => {
    const { error } = action;
    return { ...state, updatingSegment: false, updatedSegment: null, updateSegmentError: error };
};

export const segmentFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingSegment: false, segmentSuccess: null, segmentError: error }
}

export const segmentSearchFailure = (state, action) => {
    const { error } = action;
    return {
        ...state,
        searchingSegments: false,
        segmentSearchResults: null,
        segmentSearchError: error,
    };
};

export const segmentCountFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingSegmentCount: false, segmentCountSuccess: null, segmentCountError: error }
}

export const addSingleSubToSegmentFailure = (state, action) => {
    const { error } = action;
    return { ...state, addingSingleSubToSegment: false, addSingleSubToSegmentSuccess: null, addSingleSubToSegmentError: error }
}

export const bulkListToSegmentFailure = (state, action) => {
    const { error } = action
    return { ...state, addingBulkListToSegment: false, bulkListToSegment: null, bulkListToSegmentError: error }
};

export const addBulkSubToSegmentFailure = (state, action) => {
    const { error } = action;
    return { ...state, addingBulkSubToSegment: false, addBulkSubToSegmentSuccess: null, addBulkSubToSegmentError: error }
}

export const removeSubToSegmentFailure = (state, action) => {
    const { error } = action;
    return { ...state, removingSubToSegment: false, removeSubToSegmentSuccess: null, removeSubToSegmentError: error }
}

export const removeBulkSubToSegmentFailure = (state, action) => {
    const { error } = action;
    return { ...state, removingBulkSubToSegment: false, removeBulkSubToSegmentSuccess: null, removeBulkSubToSegmentError: error }
}

export const businessCustomerBulkPlanInviteFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingBulkPlanInvite: false, bulkPlanInvite: null, bulkPlanInviteFailure: error }
}

export const businessCustomerBulkResendInvitationFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingBulkPlanInviteResend: false, bulkPlanInviteResend: null, bulkPlanInviteResendFailure: error }
}

export const businessCustomerBulkInvitationFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingCustomerListInvitation: false, customerListSuccess: null, customerListFailure: error }

}

export const importCountFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingImportedCustomersCount: false, importCountError: error, importedCustomerCount: null }
}

export const createAudienceFailure = (state, action) => {
    const { error } = action;
    return { ...state, creatingAudience: false, createdAudience: null, createAudienceError: error }
}

export const audienceFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingAudience: false, audience: null, audienceError: error }
}

export const audienceBatchesFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingAudienceBatches: false, audienceBatches: null, audienceBatchesError: error }
}

export const audienceMembersFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingAudienceMembers: false, audienceMembers: null, audienceMembersError: error }
}

export const customerEnrichmentsFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingCustomerEnrichments: false, customerEnrichments: null, customerEnrichmentsError: error }
}

export const customerEnrichmentItemsFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingCustomerEnrichmentItems: false, customerEnrichmentItems: null, customerEnrichmentItemsError: error }
}

export const customerEnrichmentUploadFailure = (state, action) => {
    const { error } = action;
    return { ...state, uploadingCustomerEnrichment: false, enrichmentUpload: null, enrichmentUploadError: error }
}

export const addAudienceMemberFailure = (state, action) => {
    const { error } = action;
    return { ...state, addingAudienceMember: false, addedAudienceMember: null, addAudienceMemberError: error }
}

export const removeAudienceMemberFailure = (state, action) => {
    const { error } = action;
    return { ...state, removingAudienceMember: false, removedAudienceMember: null, removeAudienceMemberError: error }
}

export const audienceCandidateSegmentsFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingAudienceCandidateSegments: false, audienceCandidateSegments: null, audienceCandidateSegmentsError: error }
}

export const audienceCandidateTagsFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingAudienceCandidateTags: false, audienceCandidateTags: null, audienceCandidateTagsError: error }
}

export const saveAudienceCandidateMetadataFailure = (state, action) => {
    const { error } = action;
    return { ...state, savingAudienceCandidateMetadata: false, audienceCandidateMetadataSaved: null, saveAudienceCandidateMetadataError: error }
}

export const applyAudienceCandidatesFailure = (state, action) => {
    const { error } = action;
    return { ...state, applyingAudienceCandidates: false, audienceCandidatesApplied: null, applyAudienceCandidatesError: error }
}

export const deleteAudienceCandidateSegmentFailure = (state, action) => {
    const { error } = action;
    return { ...state, deletingAudienceCandidateSegment: false, deletedAudienceCandidateSegment: null, deleteAudienceCandidateSegmentError: error }
}

export const deleteAudienceCandidateTagFailure = (state, action) => {
    const { error } = action;
    return { ...state, deletingAudienceCandidateTag: false, deletedAudienceCandidateTag: null, deleteAudienceCandidateTagError: error }
}

export const entryFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingEntries: false, entryError: error }
}

export const applyFailure = (state, action) => {
    const { error, batchNo } = action;
    const applyingBatchNos = batchNo != null && batchNo !== ""
        ? (state.applyingBatchNos || []).filter((item) => String(item) !== String(batchNo))
        : [];
    return {
        ...state,
        applying: applyingBatchNos.length > 0,
        applyingBatchNos,
        applySuccess: null,
        appliedImportError: error,
    }
}

export const businessCustomerSummarySearchFailure = (state, action) => {
    const { error } = action
    return { ...state, fetchingBusinessCustomerSummarySearch: false, businessCustomerSummarySearchSuccess: null, businessCustomerSummarySearchError: error }
}
export const businessCustomersBulkPauseFailure = (state, action) => {
    const { error } = action
    return { ...state, pausingBulkBusinessCustomers: false, businessCustomersBulkPause: null, businessCustomersBulkPauseError: error }
}
export const businessCustomersBulkCancelFailure = (state, action) => {
    const { error } = action
    return { ...state, cancellingBulkBusinessCustomers: false, businessCustomersBulkCancel: null, businessCustomersBulkCancelError: error }
}
export const businessCustomersSinglePauseFailure = (state, action) => {
    const { error } = action
    return { ...state, pausingBusinessCustomersSinglePause: false, businessCustomersSinglePause: null, businessCustomersSinglePauseError: error }
}
export const businessCustomersSingleCancelFailure = (state, action) => {
    const { error } = action
    return { ...state, cancellingSingleBusinessCustomer: false, businessCustomersSingleCancel: null, businessCustomersSingleCancelError: error }
}

export const businessCustomerSummaryFailure = (state, action) => {
    const { error } = action
    return { ...state, fetchingBusinessCustomerSummary: false, businessCustomerSummarySuccess: null, businessCustomerSummaryError: error }
}

export const customerSummaryFailure = (state, action) => {
    const { error } = action
    return { ...state, fetchingCustomerSummary: false, customerSummarySuccess: null, customerSummaryError: error }
}

export const businessCustomerPaymentsFailure = (state, action) => {
    const { error } = action
    return { ...state, fetchingRecentPayments: false, recentPayments: null, recentPaymentsError: error }
}
export const businessCustomerMissedPaymentsFailure = (state, action) => {
    const { error } = action
    return { ...state, fetchingRecentMissedPayments: false, recentMissedPayments: null, recentMissedPaymentsError: error }
}
export const businessCustomerSubscriptionActivityFailure = (state, action) => {
    const { error } = action
    return { ...state, fetchingRecentSubscriptionActivity: false, recentSubscriptionActivity: null, recentSubscriptionActivityError: error }
}

export const customerPendingPaymentFailure = (state, action) => {
    const { error } = action
    return { ...state, fetchingCustomerPendingPayments: false, customerPendingPayments: null, customerPendingPaymentsError: error }
}

export const customerMissedPaymentFailure = (state, action) => {
    const { error } = action
    return { ...state, fetchingCustomerMissedPayments: false, customerMissedPayments: null, customerMissedPaymentsError: error }
}

export const customerReceivedPaymentFailure = (state, action) => {
    const { error } = action
    return { ...state, fetchingCustomerReceivedPayments: false, customerReceivedPayments: null, customerReceivedPaymentsError: error }
}

export const customerReportsFailure = (state, action) => {
    const { error } = action
    return { ...state, fetchingCustomerReports: false, customerReports: null, customerReportsError: error }
}
export const customerChartsFailure = (state, action) => {
    const { error } = action
    return { ...state, fetchingCustomerCharts: false, customerCharts: null, customerChartsError: error }
}
export const successfulPaymentsFailure = (state, action) => {
    const { error } = action
    return { ...state, fetchingSuccessfulPayments: false, successfulPayments: null, successfulPaymentsError: error }
}
export const expiredPaymentsFailure = (state, action) => {
    const { error } = action
    return { ...state, fetchingExpiredPayments: false, expiredPayments: null, expiredPaymentsError: error }
}
//something went wrong fetching businessCustomerSubscriptionAmounts?
export const businessCustomerSubscriptionAmountsFailure = (state, action) => {
    const { error } = action;
    return {
        ...state, fetchingBusinessCustomerSubscriptionAmounts: false, businessCustomerSubscriptionAmountsError: error,
        businessCustomerSubscriptionAmounts: null
    }
};

// Something went wrong requesting for tag groups graphed.
export const tagGroupGraphedFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingTagGroupsGraphed: false, tagGroupsGraphedError: error, tagGroupsGraphed: null };
};

// Something went wrong creating tag.
export const createTagGroupFailure = (state, action) => {
    const { error } = action;
    return { ...state, creatingTagGroup: false, createTagGroupError: error, createdTagGroup: null, };
};

// Something went wrong updating tag group?
export const updateTagGroupFailure = (state, action) => {
    const { error } = action;
    return { ...state, updatingTagGroup: false, updateTagGroupError: error, updatedTagGroup: {} };
};

// Something went wrong requesting for tag groups.
export const tagGroupFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingTagGroups: false, tagGroupsError: error, tagGroups: null };
};

export const tagGroupSearchFailure = (state, action) => {
    const { error } = action;
    return {
        ...state,
        searchingTagGroups: false,
        tagGroupSearchResults: null,
        tagGroupSearchError: error,
    };
};

// Something went wrong creating tag.
export const createTagFailure = (state, action) => {
    const { error } = action;
    return { ...state, creatingTag: false, createTagError: error, createdTag: null, };
};

// Something went wrong requesting for tags.
export const tagsFailure = (state, action) => {
    const { error } = action;
    return { ...state, fetchingTags: false, tagsError: error, tags: null };
};

// Something went wrong updating tag group?
export const updateTagFailure = (state, action) => {
    const { error } = action;
    return { ...state, updatingTag: false, updateTagError: error, updatedTag: {} };
};

// Something went wrong associating a tag.
export const associateSubscriberToTagFailure = (state, action) => {
    const { error } = action;
    return { ...state, associatingTag: false, associatingTagError: error, associatedTag: null };
};
// Something went wrong bulk associating a tag.
export const bulkAssociateSubscriberToTagFailure = (state, action) => {
    const { error } = action;
    return { ...state, bulkAssociatingTag: false, bulkAssociatingTagError: error, bulkAssociatedTag: null };
};
// Something went wrong dissociating a tag.
export const dissociateSubscriberFromTagFailure = (state, action) => {
    const { error } = action;
    return { ...state, dissociatingTag: false, dissociatingTagError: error, dissociatedTag: null };
};

// Something went wrong bulk dissociating a tag.
export const bulkDissociateSubscribersFromTagFailure = (state, action) => {
    const { error } = action;
    return { ...state, bulkDissociatingTag: false, bulkDissociatingTagError: error, bulkDissociatedTag: null };
};

/* ------------- Hookup Reducers To Types ------------- */
export const reducer = createReducer(INITIAL_STATE, {
    [Types.BUSINESS_CUSTOMER_ALL_REQUEST]: allRequest,
    [Types.BUSINESS_CUSTOMER_SEARCH_REQUEST]: customerSearchRequest,
    [Types.EXACT_FILTERED_BUSINESS_CUSTOMER_REQUEST]: exactFilteredBusinessCustomerRequest,
    [Types.BUSINESS_CUSTOMER_UPDATE_REQUEST]: updateRequest,
    [Types.BUSINESS_CUSTOMER_SUBSCRIPTIONS_REQUEST]: businessCustomerSubscriptionsRequest,
    [Types.BUSINESS_CUSTOMER_RECORD_REQUEST]: businessCustomerRecordRequest,
    [Types.BUSINESS_CUSTOMER_SUBSCRIPTIONS_COUNT_REQUEST]: countRequest,
    [Types.BUSINESS_CUSTOMER_SUMMARY_SEARCH_REQUEST]: businessCustomerSummarySearchRequest,
    [Types.BUSINESS_CUSTOMER_SUMMARY_REQUEST]: businessCustomerSummaryRequest,
    [Types.CUSTOMER_SUMMARY_REQUEST]: customerSummaryRequest,
    [Types.BUSINESS_CUSTOMER_UPLOAD_REQUEST]: uploadRequest,
    [Types.BUSINESS_CUSTOMER_IMPORT_REQUEST]: importRequest,
    [Types.BUSINESS_CUSTOMER_IMPORT_COUNT_REQUEST]: importCountRequest,
    [Types.CREATE_AUDIENCE_REQUEST]: createAudienceRequest,
    [Types.AUDIENCE_REQUEST]: audienceRequest,
    [Types.AUDIENCE_BATCHES_REQUEST]: audienceBatchesRequest,
    [Types.AUDIENCE_MEMBERS_REQUEST]: audienceMembersRequest,
    [Types.CUSTOMER_ENRICHMENTS_REQUEST]: customerEnrichmentsRequest,
    [Types.CUSTOMER_ENRICHMENT_ITEMS_REQUEST]: customerEnrichmentItemsRequest,
    [Types.CUSTOMER_ENRICHMENT_UPLOAD_REQUEST]: customerEnrichmentUploadRequest,
    [Types.ADD_AUDIENCE_MEMBER_REQUEST]: addAudienceMemberRequest,
    [Types.REMOVE_AUDIENCE_MEMBER_REQUEST]: removeAudienceMemberRequest,
    [Types.AUDIENCE_CANDIDATE_SEGMENTS_REQUEST]: audienceCandidateSegmentsRequest,
    [Types.AUDIENCE_CANDIDATE_TAGS_REQUEST]: audienceCandidateTagsRequest,
    [Types.SAVE_AUDIENCE_CANDIDATE_METADATA_REQUEST]: saveAudienceCandidateMetadataRequest,
    [Types.APPLY_AUDIENCE_CANDIDATES_REQUEST]: applyAudienceCandidatesRequest,
    [Types.DELETE_AUDIENCE_CANDIDATE_SEGMENT_REQUEST]: deleteAudienceCandidateSegmentRequest,
    [Types.DELETE_AUDIENCE_CANDIDATE_TAG_REQUEST]: deleteAudienceCandidateTagRequest,
    [Types.BUSINESS_CUSTOMER_ENTRY_REQUEST]: entryRequest,
    [Types.BUSINESS_CUSTOMER_APPLY_REQUEST]: applyRequest,
    [Types.BUSINESS_CUSTOMER_PAYMENTS_REQUEST]: businessCustomerPaymentsRequest,
    [Types.BUSINESS_CUSTOMER_MISSED_PAYMENTS_REQUEST]: businessCustomerMissedPaymentsRequest,
    [Types.BUSINESS_CUSTOMER_SUBSCRIPTION_ACTIVITY_REQUEST]: businessCustomerSubscriptionActivityRequest,
    [Types.CUSTOMER_RECEIVED_PAYMENT_REQUEST]: customerReceivedPaymentRequest,
    [Types.CUSTOMER_MISSED_PAYMENT_REQUEST]: customerMissedPaymentRequest,
    [Types.CUSTOMER_PENDING_PAYMENT_REQUEST]: customerPendingPaymentRequest,
    [Types.CUSTOMER_REPORT_REQUEST]: customerReportsRequest,
    [Types.CUSTOMER_CHARTS_REQUEST]: customerChartsRequest,
    [Types.SUCCESSFUL_PAYMENTS_REQUEST]: successfulPaymentsRequest,
    [Types.EXPIRED_PAYMENTS_REQUEST]: expiredPaymentsRequest,
    [Types.BUSINESS_CUSTOMERS_BULK_PAUSE_REQUEST]: businessCustomersBulkPauseRequest,
    [Types.BUSINESS_CUSTOMERS_BULK_CANCEL_REQUEST]: businessCustomersBulkCancelRequest,
    [Types.BUSINESS_CUSTOMERS_SINGLE_PAUSE_REQUEST]: businessCustomersSinglePauseRequest,
    [Types.BUSINESS_CUSTOMERS_SINGLE_CANCEL_REQUEST]: businessCustomersSingleCancelRequest,
    [Types.BUSINESS_CUSTOMER_SUBSCRIPTION_AMOUNTS_REQUEST]: businessCustomerSubscriptionAmountsRequest,
    [Types.DOWNLOAD_CSV_REQUEST]: downloadCsvRequest,
    [Types.EXPORT_BUSINESS_CUSTOMERS_CSV_REQUEST]: exportBusinessCustomersCsvRequest,
    [Types.BUSINESS_CUSTOMER_STATEMENT_REQUEST]: businessCustomerStatementRequest,
    [Types.BUSINESS_CUSTOMER_STATEMENT_CSV_REQUEST]: businessCustomerStatementCsvRequest,
    [Types.BUSINESS_CUSTOMER_STATEMENT_PDF_REQUEST]: businessCustomerStatementPdfRequest,
    [Types.DOWNLOAD_AUDIENCE_MEMBERS_CSV_REQUEST]: downloadAudienceMembersCsvRequest,
    [Types.BUSINESS_CUSTOMER_BULK_PLAN_INVITE_REQUEST]: businessCustomerBulkPlanInviteRequest,
    [Types.BUSINESS_CUSTOMER_BULK_INVITATION_REQUEST]: businessCustomerBulkInvitationRequest,
    [Types.CREATE_SEGMENT_REQUEST]: createSegmentRequest,
    [Types.UPDATE_SEGMENT_REQUEST]: updateSegmentRequest,
    [Types.FETCHING_SEGMENT_REQUEST]: fetchingSegmentRequest,
    [Types.SEGMENT_SEARCH_REQUEST]: segmentSearchRequest,
    [Types.FETCHING_SEGMENT_COUNT_REQUEST]: fetchingSegmentCountRequest,
    [Types.ADDING_SINGLE_SUB_TO_SEGMENT_REQUEST]: addingSingleSubToSegmentRequest,
    [Types.ADDING_BULK_SUB_TO_SEGMENT_REQUEST]: addingBulkSubToSegmentRequest,
    [Types.REMOVING_SUB_TO_SEGMENT_REQUEST]: removingSubToSegmentRequest,
    [Types.REMOVING_BULK_SUB_TO_SEGMENT_REQUEST]: removingBulkSubToSegmentRequest,
    [Types.TAG_GROUP_GRAPHED_REQUEST]: tagGroupGraphedRequest,
    [Types.CREATE_TAG_GROUP_REQUEST]: createTagGroupRequest,
    [Types.UPDATE_TAG_GROUP_REQUEST]: updateTagGroupRequest,
    [Types.TAG_GROUP_REQUEST]: tagGroupRequest,
    [Types.TAG_GROUP_SEARCH_REQUEST]: tagGroupSearchRequest,
    [Types.CREATE_TAG_REQUEST]: createTagRequest,
    [Types.UPDATE_TAG_REQUEST]: updateTagRequest,
    [Types.TAGS_REQUEST]: tagsRequest,
    [Types.ASSOCIATE_SUBSCRIBER_TO_TAG_REQUEST]: associateSubscriberToTagRequest,
    [Types.BULK_ASSOCIATE_SUBSCRIBER_TO_TAG_REQUEST]: bulkAssociateSubscriberToTagRequest,
    [Types.DISSOCIATE_SUBSCRIBER_FROM_TAG_REQUEST]: dissociateSubscriberFromTagRequest,
    [Types.BULK_DISSOCIATE_SUBSCRIBERS_FROM_TAG_REQUEST]: bulkDissociateSubscribersFromTagRequest,
    [Types.BUSINESS_CUSTOMER_BULK_RESEND_INVITATION_REQUEST]: businessCustomerBulkResendInvitationRequest,
    [Types.ADDING_BULK_LIST_TO_SEGMENT_REQUEST]: addingBulkListToSegmentRequest,
    [Types.CREATE_BUSINESS_CUSTOMER_REQUEST]: createBusinessCustomerRequest,
    [Types.LINK_PLAN_WITH_BUSINESS_CUSTOMER_REQUEST]: linkPlanWithBusinessCustomerRequest,
    [Types.LINK_BUSINESS_CUSTOMER_TO_PRIMARY_REQUEST]: linkBusinessCustomerToPrimaryRequest,
    [Types.UNLINK_BUSINESS_CUSTOMER_FROM_PRIMARY_REQUEST]: unlinkBusinessCustomerFromPrimaryRequest,

    [Types.BUSINESS_CUSTOMER_ALL_SUCCESS]: allSuccess,
    [Types.BUSINESS_CUSTOMER_SEARCH_SUCCESS]: customerSearchSuccess,
    [Types.EXACT_FILTERED_BUSINESS_CUSTOMER_SUCCESS]: exactFilteredBusinessCustomerSuccess,
    [Types.BUSINESS_CUSTOMER_UPDATE_SUCCESS]: updateSuccess,
    [Types.BUSINESS_CUSTOMER_SUBSCRIPTIONS_SUCCESS]: businessCustomerSubcriptionsSuccess,
    [Types.BUSINESS_CUSTOMER_SUMMARY_SEARCH_SUCCESS]: businessCustomerSummarySearchSuccess,
    [Types.BUSINESS_CUSTOMER_SUMMARY_SUCCESS]: businessCustomerSummarySuccess,
    [Types.CUSTOMER_SUMMARY_SUCCESS]: customerSummarySuccess,
    [Types.BUSINESS_CUSTOMER_RECORD_SUCCESS]: businessCustomerRecordSuccess,
    [Types.BUSINESS_CUSTOMER_SUBSCRIPTIONS_COUNT_SUCCESS]: countSuccess,
    [Types.BUSINESS_CUSTOMER_UPLOAD_SUCCESS]: uploadSuccess,
    [Types.BUSINESS_CUSTOMER_IMPORT_SUCCESS]: importSuccess,
    [Types.BUSINESS_CUSTOMER_IMPORT_COUNT_SUCCESS]: importCountSuccess,
    [Types.CREATE_AUDIENCE_SUCCESS]: createAudienceSuccess,
    [Types.AUDIENCE_SUCCESS]: audienceSuccess,
    [Types.AUDIENCE_BATCHES_SUCCESS]: audienceBatchesSuccess,
    [Types.AUDIENCE_MEMBERS_SUCCESS]: audienceMembersSuccess,
    [Types.CUSTOMER_ENRICHMENTS_SUCCESS]: customerEnrichmentsSuccess,
    [Types.CUSTOMER_ENRICHMENT_ITEMS_SUCCESS]: customerEnrichmentItemsSuccess,
    [Types.CUSTOMER_ENRICHMENT_UPLOAD_SUCCESS]: customerEnrichmentUploadSuccess,
    [Types.ADD_AUDIENCE_MEMBER_SUCCESS]: addAudienceMemberSuccess,
    [Types.REMOVE_AUDIENCE_MEMBER_SUCCESS]: removeAudienceMemberSuccess,
    [Types.AUDIENCE_CANDIDATE_SEGMENTS_SUCCESS]: audienceCandidateSegmentsSuccess,
    [Types.AUDIENCE_CANDIDATE_TAGS_SUCCESS]: audienceCandidateTagsSuccess,
    [Types.SAVE_AUDIENCE_CANDIDATE_METADATA_SUCCESS]: saveAudienceCandidateMetadataSuccess,
    [Types.APPLY_AUDIENCE_CANDIDATES_SUCCESS]: applyAudienceCandidatesSuccess,
    [Types.DELETE_AUDIENCE_CANDIDATE_SEGMENT_SUCCESS]: deleteAudienceCandidateSegmentSuccess,
    [Types.DELETE_AUDIENCE_CANDIDATE_TAG_SUCCESS]: deleteAudienceCandidateTagSuccess,
    [Types.BUSINESS_CUSTOMER_ENTRY_SUCCESS]: entrySuccess,
    [Types.BUSINESS_CUSTOMER_APPLY_SUCCESS]: applySuccess,
    [Types.BUSINESS_CUSTOMER_PAYMENTS_SUCCESS]: businessCustomerPaymentsSuccess,
    [Types.BUSINESS_CUSTOMER_MISSED_PAYMENTS_SUCCESS]: businessCustomerMissedPaymentsSuccess,
    [Types.BUSINESS_CUSTOMER_SUBSCRIPTION_ACTIVITY_SUCCESS]: businessCustomerSubscriptionActivitySuccess,
    [Types.CUSTOMER_RECEIVED_PAYMENT_SUCCESS]: customerReceivedPaymentSuccess,
    [Types.CUSTOMER_MISSED_PAYMENT_SUCCESS]: customerMissedPaymentSuccess,
    [Types.CUSTOMER_PENDING_PAYMENT_SUCCESS]: customerPendingPaymentSuccess,
    [Types.CUSTOMER_REPORT_SUCCESS]: customerReportsSuccess,
    [Types.CUSTOMER_CHARTS_SUCCESS]: customerChartsSuccess,
    [Types.SUCCESSFUL_PAYMENTS_SUCCESS]: successfulPaymentsSuccess,
    [Types.EXPIRED_PAYMENTS_SUCCESS]: expiredPaymentsSuccess,
    [Types.BUSINESS_CUSTOMERS_BULK_PAUSE_SUCCESS]: businessCustomersBulkPauseSuccess,
    [Types.BUSINESS_CUSTOMERS_BULK_CANCEL_SUCCESS]: businessCustomersBulkCancelSuccess,
    [Types.BUSINESS_CUSTOMERS_SINGLE_PAUSE_SUCCESS]: businessCustomersSinglePauseSuccess,
    [Types.BUSINESS_CUSTOMERS_SINGLE_CANCEL_SUCCESS]: businessCustomersSingleCancelSuccess,
    [Types.BUSINESS_CUSTOMER_SUBSCRIPTION_AMOUNTS_SUCCESS]: businessCustomerSubscriptionAmountsSuccess,
    [Types.DOWNLOAD_CSV_SUCCESS]: downloadCsvSuccess,
    [Types.EXPORT_BUSINESS_CUSTOMERS_CSV_SUCCESS]: exportBusinessCustomersCsvSuccess,
    [Types.BUSINESS_CUSTOMER_STATEMENT_SUCCESS]: businessCustomerStatementSuccess,
    [Types.BUSINESS_CUSTOMER_STATEMENT_CSV_SUCCESS]: businessCustomerStatementCsvSuccess,
    [Types.BUSINESS_CUSTOMER_STATEMENT_PDF_SUCCESS]: businessCustomerStatementPdfSuccess,
    [Types.DOWNLOAD_AUDIENCE_MEMBERS_CSV_SUCCESS]: downloadAudienceMembersCsvSuccess,
    [Types.BUSINESS_CUSTOMER_BULK_PLAN_INVITE_SUCCESS]: businessCustomerBulkPlanInviteSuccess,
    [Types.BUSINESS_CUSTOMER_BULK_INVITATION_SUCCESS]: businessCustomerBulkInvitationSuccess,
    [Types.SEGMENT_CREATE_SUCCESS]: segmentCreateSuccess,
    [Types.UPDATE_SEGMENT_SUCCESS]: updateSegmentSuccess,
    [Types.SEGMENT_SUCCESS]: segmentSuccess,
    [Types.SEGMENT_SEARCH_SUCCESS]: segmentSearchSuccess,
    [Types.SEGMENT_COUNT_SUCCESS]: segmentCountSuccess,
    [Types.ADD_SINGLE_SUB_TO_SEGMENT_SUCCESS]: addSingleSubToSegmentSuccess,
    [Types.ADD_BULK_SUB_TO_SEGMENT_SUCCESS]: addBulkSubToSegmentSuccess,
    [Types.REMOVE_SUB_TO_SEGMENT_SUCCESS]: removeSubToSegmentSuccess,
    [Types.REMOVE_BULK_SUB_TO_SEGMENT_SUCCESS]: removeBulkSubToSegmentSuccess,
    [Types.TAG_GROUP_GRAPHED_SUCCESS]: tagGroupGraphedSuccess,
    [Types.CREATE_TAG_GROUP_SUCCESS]: createTagGroupSuccess,
    [Types.UPDATE_TAG_GROUP_SUCCESS]: updateTagGroupSuccess,
    [Types.TAG_GROUP_SUCCESS]: tagGroupSuccess,
    [Types.TAG_GROUP_SEARCH_SUCCESS]: tagGroupSearchSuccess,
    [Types.CREATE_TAG_SUCCESS]: createTagSuccess,
    [Types.UPDATE_TAG_SUCCESS]: updateTagSuccess,
    [Types.TAGS_SUCCESS]: tagsSuccess,
    [Types.ASSOCIATE_SUBSCRIBER_TO_TAG_SUCCESS]: associateSubscriberToTagSuccess,
    [Types.BULK_ASSOCIATE_SUBSCRIBER_TO_TAG_SUCCESS]: bulkAssociateSubscriberToTagSuccess,
    [Types.DISSOCIATE_SUBSCRIBER_FROM_TAG_SUCCESS]: dissociateSubscriberFromTagSuccess,
    [Types.BULK_DISSOCIATE_SUBSCRIBERS_FROM_TAG_SUCCESS]: bulkDissociateSubscribersFromTagSuccess,
    [Types.BUSINESS_CUSTOMER_BULK_RESEND_INVITATION_SUCCESS]: businessCustomerBulkResendInvitationSuccess,
    [Types.BULK_LIST_TO_SEGMENT_SUCCESS]: bulkListToSegmentSuccess,
    [Types.CREATE_BUSINESS_CUSTOMER_SUCCESS]: createBusinessCustomerSuccess,
    [Types.LINK_PLAN_WITH_BUSINESS_CUSTOMER_SUCCESS]: linkPlanWithBusinessCustomerSuccess,
    [Types.LINK_BUSINESS_CUSTOMER_TO_PRIMARY_SUCCESS]: linkBusinessCustomerToPrimarySuccess,
    [Types.UNLINK_BUSINESS_CUSTOMER_FROM_PRIMARY_SUCCESS]: unlinkBusinessCustomerFromPrimarySuccess,

    [Types.BUSINESS_CUSTOMER_ALL_FAILURE]: allFailure,
    [Types.BUSINESS_CUSTOMER_SEARCH_FAILURE]: customerSearchFailure,
    [Types.EXACT_FILTERED_BUSINESS_CUSTOMERS_FAILURE]: exactFilteredBusinessCustomersFailure,
    [Types.BUSINESS_CUSTOMER_UPDATE_FAILURE]: updateFailure,
    [Types.BUSINESS_CUSTOMER_SUBSCRIPTIONS_FAILURE]: businessCustomerRecordFailure,
    [Types.BUSINESS_CUSTOMER_RECORD_FAILURE]: businessCustomerRecordFailure,
    [Types.BUSINESS_CUSTOMER_SUBSCRIPTIONS_COUNT_FAILURE]: countFailure,
    [Types.BUSINESS_CUSTOMER_SUMMARY_FAILURE]: businessCustomerSummaryFailure,
    [Types.CUSTOMER_SUMMARY_FAILURE]: customerSummaryFailure,
    [Types.BUSINESS_CUSTOMER_UPLOAD_FAILURE]: uploadFailure,
    [Types.BUSINESS_CUSTOMER_IMPORT_FAILURE]: importFailure,
    [Types.BUSINESS_CUSTOMER_IMPORT_COUNT_FAILURE]: importCountFailure,
    [Types.CREATE_AUDIENCE_FAILURE]: createAudienceFailure,
    [Types.AUDIENCE_FAILURE]: audienceFailure,
    [Types.AUDIENCE_BATCHES_FAILURE]: audienceBatchesFailure,
    [Types.AUDIENCE_MEMBERS_FAILURE]: audienceMembersFailure,
    [Types.CUSTOMER_ENRICHMENTS_FAILURE]: customerEnrichmentsFailure,
    [Types.CUSTOMER_ENRICHMENT_ITEMS_FAILURE]: customerEnrichmentItemsFailure,
    [Types.CUSTOMER_ENRICHMENT_UPLOAD_FAILURE]: customerEnrichmentUploadFailure,
    [Types.ADD_AUDIENCE_MEMBER_FAILURE]: addAudienceMemberFailure,
    [Types.REMOVE_AUDIENCE_MEMBER_FAILURE]: removeAudienceMemberFailure,
    [Types.AUDIENCE_CANDIDATE_SEGMENTS_FAILURE]: audienceCandidateSegmentsFailure,
    [Types.AUDIENCE_CANDIDATE_TAGS_FAILURE]: audienceCandidateTagsFailure,
    [Types.SAVE_AUDIENCE_CANDIDATE_METADATA_FAILURE]: saveAudienceCandidateMetadataFailure,
    [Types.APPLY_AUDIENCE_CANDIDATES_FAILURE]: applyAudienceCandidatesFailure,
    [Types.DELETE_AUDIENCE_CANDIDATE_SEGMENT_FAILURE]: deleteAudienceCandidateSegmentFailure,
    [Types.DELETE_AUDIENCE_CANDIDATE_TAG_FAILURE]: deleteAudienceCandidateTagFailure,
    [Types.BUSINESS_CUSTOMER_ENTRY_FAILURE]: entryFailure,
    [Types.BUSINESS_CUSTOMER_APPLY_FAILURE]: applyFailure,
    [Types.BUSINESS_CUSTOMER_PAYMENTS_FAILURE]: businessCustomerPaymentsFailure,
    [Types.BUSINESS_CUSTOMER_MISSED_PAYMENTS_FAILURE]: businessCustomerMissedPaymentsFailure,
    [Types.BUSINESS_CUSTOMER_SUBSCRIPTION_ACTIVITY_FAILURE]: businessCustomerSubscriptionActivityFailure,
    [Types.CUSTOMER_RECEIVED_PAYMENT_FAILURE]: customerReceivedPaymentFailure,
    [Types.CUSTOMER_MISSED_PAYMENT_FAILURE]: customerMissedPaymentFailure,
    [Types.CUSTOMER_PENDING_PAYMENT_FAILURE]: customerPendingPaymentFailure,
    [Types.CUSTOMER_REPORT_FAILURE]: customerReportsFailure,
    [Types.CUSTOMER_CHARTS_FAILURE]: customerChartsFailure,
    [Types.SUCCESSFUL_PAYMENTS_FAILURE]: successfulPaymentsFailure,
    [Types.EXPIRED_PAYMENTS_FAILURE]: expiredPaymentsFailure,
    [Types.BUSINESS_CUSTOMERS_BULK_PAUSE_FAILURE]: businessCustomersBulkPauseFailure,
    [Types.BUSINESS_CUSTOMERS_BULK_CANCEL_FAILURE]: businessCustomersBulkCancelFailure,
    [Types.BUSINESS_CUSTOMERS_SINGLE_PAUSE_FAILURE]: businessCustomersSinglePauseFailure,
    [Types.BUSINESS_CUSTOMERS_SINGLE_CANCEL_FAILURE]: businessCustomersSingleCancelFailure,
    [Types.BUSINESS_CUSTOMER_SUBSCRIPTION_AMOUNTS_FAILURE]: businessCustomerSubscriptionAmountsFailure,
    [Types.DOWNLOAD_CSV_FAILURE]: downloadCsvFailure,
    [Types.EXPORT_BUSINESS_CUSTOMERS_CSV_FAILURE]: exportBusinessCustomersCsvFailure,
    [Types.BUSINESS_CUSTOMER_STATEMENT_FAILURE]: businessCustomerStatementFailure,
    [Types.BUSINESS_CUSTOMER_STATEMENT_CSV_FAILURE]: businessCustomerStatementCsvFailure,
    [Types.BUSINESS_CUSTOMER_STATEMENT_PDF_FAILURE]: businessCustomerStatementPdfFailure,
    [Types.DOWNLOAD_AUDIENCE_MEMBERS_CSV_FAILURE]: downloadAudienceMembersCsvFailure,
    [Types.BUSINESS_CUSTOMER_BULK_PLAN_INVITE_FAILURE]: businessCustomerBulkPlanInviteFailure,
    [Types.BUSINESS_CUSTOMER_BULK_INVITATION_FAILURE]: businessCustomerBulkInvitationFailure,
    [Types.SEGMENT_CREATE_FAILURE]: segmentCreateFailure,
    [Types.UPDATE_SEGMENT_FAILURE]: updateSegmentFailure,
    [Types.SEGMENT_FAILURE]: segmentFailure,
    [Types.SEGMENT_SEARCH_FAILURE]: segmentSearchFailure,
    [Types.SEGMENT_COUNT_FAILURE]: segmentCountFailure,
    [Types.ADD_SINGLE_SUB_TO_SEGMENT_FAILURE]: addSingleSubToSegmentFailure,
    [Types.BULK_LIST_TO_SEGMENT_FAILURE]: bulkListToSegmentFailure,
    [Types.ADD_BULK_SUB_TO_SEGMENT_FAILURE]: addBulkSubToSegmentFailure,
    [Types.REMOVE_SUB_TO_SEGMENT_FAILURE]: removeSubToSegmentFailure,
    [Types.REMOVE_BULK_SUB_TO_SEGMENT_FAILURE]: removeBulkSubToSegmentFailure,
    [Types.TAG_GROUP_GRAPHED_FAILURE]: tagGroupGraphedFailure,
    [Types.CREATE_TAG_GROUP_FAILURE]: createTagGroupFailure,
    [Types.UPDATE_TAG_GROUP_FAILURE]: updateTagGroupFailure,
    [Types.TAG_GROUP_FAILURE]: tagGroupFailure,
    [Types.TAG_GROUP_SEARCH_FAILURE]: tagGroupSearchFailure,
    [Types.CREATE_TAG_FAILURE]: createTagFailure,
    [Types.UPDATE_TAG_FAILURE]: updateTagFailure,
    [Types.TAGS_FAILURE]: tagsFailure,
    [Types.ASSOCIATE_SUBSCRIBER_TO_TAG_FAILURE]: associateSubscriberToTagFailure,
    [Types.BULK_ASSOCIATE_SUBSCRIBER_TO_TAG_FAILURE]: bulkAssociateSubscriberToTagFailure,
    [Types.DISSOCIATE_SUBSCRIBER_FROM_TAG_FAILURE]: dissociateSubscriberFromTagFailure,
    [Types.BULK_DISSOCIATE_SUBSCRIBERS_FROM_TAG_FAILURE]: bulkDissociateSubscribersFromTagFailure,
    [Types.BUSINESS_CUSTOMER_BULK_RESEND_INVITATION_FAILURE]: businessCustomerBulkResendInvitationFailure,
    [Types.CREATE_BUSINESS_CUSTOMER_FAILURE]: createBusinessCustomerFailure,
    [Types.LINK_PLAN_WITH_BUSINESS_CUSTOMER_FAILURE]: linkPlanWithBusinessCustomerFailure,
    [Types.LINK_BUSINESS_CUSTOMER_TO_PRIMARY_FAILURE]: linkBusinessCustomerToPrimaryFailure,
    [Types.UNLINK_BUSINESS_CUSTOMER_FROM_PRIMARY_FAILURE]: unlinkBusinessCustomerFromPrimaryFailure,
})
